import {
  computePosition,
  offset,
  shift,
  size,
  autoUpdate,
} from '@floating-ui/dom';
import {
  applySearchParamsToRoot,
  collectFromSearchRoot,
  navigateFromSearchDecision,
  navigateWithSearchParams,
  readSearchParams,
  resolveInitialSearchParams,
} from '../../../js/offerSearchParams.js';
import {
  allowsSingleDaySelection,
  captureSelection,
  findTurnusForDate,
  FERIE_GROUPS,
  generateFerieTurnusy,
  getDestinationMode,
  getSearchCatalogUrls,
  getSearchContext,
  getVisibleTypeSlugs,
  hydrateSearchCatalog,
  HOTEL_TYPE_SLUG,
  isListingSearchAction,
  mergeTurnusSlots,
  resolveFormAction,
  shouldRequireOfferType,
  shouldShowDateGuests,
  transitionSearchRules,
} from '../../../js/offerSearchRules.js';
import { isUnifiedListingContext } from '../../../js/unifiedListingContext.js';

const MONTH_NAMES_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const MONTHS_GENITIVE_PL = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
];

const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** English uses nominative month names in range titles (same list as calendar titles). */
const MONTHS_GENITIVE_EN = MONTH_NAMES_EN;

/** Hard EN when data-search-ui still ships Polish (stale prod .mo). */
const PHRASE_EN = {
  'wszystkie ośrodki': 'All resorts',
  '1 dorosły': '1 adult',
  dorosłych: 'adults',
  '1 dziecko': '1 child',
  dzieci: 'children',
  '1 noc': '1 night',
  noce: 'nights',
  nocy: 'nights',
  '1 dzień': '1 day',
  dni: 'days',
  'Brak terminów dla tego kierunku.': 'No dates for this destination.',
  'Wybierz termin wyjazdu': 'Select a trip date',
  'Wybierz termin szkolenia': 'Select a training date',
  'Wyjazd:': 'Departure:',
  'Powrót:': 'Return:',
  'Termin:': 'Date:',
  '— wybierz termin —': '— select date —',
  'Wiek dziecka': "Child's age",
  'Wiek dziecka {n}': "Child's age {n}",
  '1 rok': '1 year',
  lata: 'years',
  lat: 'years',
  Turnus: 'Week',
  Dorośli: 'Adults',
  Dzieci: 'Children',
};

const MIN_CHILD_AGE = 0;
const MAX_CHILD_AGE = 17;
const DEFAULT_CHILD_AGE = 8;

const POPOVER_ANIMATION_MS = 240;

const isUiEnglish = () => {
  if (typeof document === 'undefined') return false;
  const lang = (document.documentElement?.lang || '').toLowerCase();
  if (lang.startsWith('en')) return true;
  const body = document.body;
  if (body?.classList?.contains('hski-lang-en') || body?.classList?.contains('pll-en')) {
    return true;
  }
  return (window.location?.pathname || '').startsWith('/en/');
};

/** WordPress `hskiI18n` — secondary fallback when data-search-ui phrases/calendar are missing. */
const msgFromHski = (polish) => {
  const bag = typeof window !== 'undefined' ? window.hskiI18n : null;
  if (!bag || typeof polish !== 'string' || polish === '') return '';
  const fromMsg = bag.msg?.[polish];
  if (typeof fromMsg === 'string' && fromMsg !== '') return fromMsg;
  const aliases = {
    dorosłych: bag.adult_many,
    dorosły: bag.adult_one,
    '1 dorosły': bag.adult_one ? `1 ${bag.adult_one}` : '',
    dzieci: bag.child_many,
    dziecko: bag.child_one,
    '1 dziecko': bag.child_one ? `1 ${bag.child_one}` : '',
    Dorośli: bag.adults_label,
    Dzieci: bag.children_label,
    '1 noc': bag.night_one,
    noce: typeof bag.night_few === 'string' ? bag.night_few.replace('%d ', '') : '',
    nocy: typeof bag.night_many === 'string' ? bag.night_many.replace('%d ', '') : '',
    '1 dzień': bag.day_one,
    dni: typeof bag.day_many === 'string' ? bag.day_many.replace('%d ', '') : '',
  };
  const hit = aliases[polish];
  return typeof hit === 'string' && hit !== '' ? hit : '';
};

const enForPolish = (polish) => {
  if (typeof polish !== 'string' || polish === '') return '';
  if (PHRASE_EN[polish]) return PHRASE_EN[polish];
  const mi = MONTH_NAMES_PL.indexOf(polish);
  if (mi >= 0) return MONTH_NAMES_EN[mi];
  const gi = MONTHS_GENITIVE_PL.indexOf(polish);
  if (gi >= 0) return MONTHS_GENITIVE_EN[gi];
  return msgFromHski(polish) || '';
};

/**
 * Prefer data-search-ui / hskiI18n; on EN pages never keep an untranslated Polish value.
 */
const i18nOr = (value, polishFallback) => {
  const en = isUiEnglish();
  if (typeof value === 'string' && value !== '') {
    if (en) {
      if (value === polishFallback) return enForPolish(polishFallback) || value;
      const mapped = enForPolish(value);
      if (mapped && mapped !== value) return mapped;
    }
    return value;
  }
  if (en) return enForPolish(polishFallback) || polishFallback;
  return msgFromHski(polishFallback) || polishFallback;
};

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
);

const parseDate = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDisplayDate = (date) => {
  if (!date) return '';
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const formatRangeLabel = (start, end, emptyLabel = '') => {
  if (!start || !end) {
    return emptyLabel || i18nOr('', '— wybierz termin —');
  }
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.getDate()}.${String(start.getMonth() + 1).padStart(2, '0')} - ${end.getDate()}.${String(end.getMonth() + 1).padStart(2, '0')}.${end.getFullYear()}`;
  }
  return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
};

const closeDropdown = (root, wrap, { immediate = false } = {}) => {
  const trigger = wrap.querySelector('.offer-search__field');
  const panel = wrap.querySelector('.offer-search__popover');

  wrap._closeTimer?.();
  wrap._closeTimer = null;

  if (!wrap.classList.contains('is-open') && !wrap.classList.contains('is-closing')) {
    if (panel) panel.hidden = true;
    return;
  }

  wrap.classList.remove('is-open');
  trigger?.setAttribute('aria-expanded', 'false');
  if (wrap.dataset.dropdown === 'guests') {
    closeAllAgeSelects(wrap);
  }

  if (!panel || immediate || prefersReducedMotion()) {
    wrap.classList.remove('is-closing');
    if (panel) panel.hidden = true;
    syncSearchOpenState(root);
    return;
  }

  wrap.classList.add('is-closing');
  syncSearchOpenState(root);

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    panel.removeEventListener('transitionend', onTransitionEnd);
    wrap.classList.remove('is-closing');
    if (!wrap.classList.contains('is-open')) {
      panel.hidden = true;
    }
    wrap._closeTimer = null;
    syncSearchOpenState(root);
  };

  const onTransitionEnd = (event) => {
    if (event.target !== panel) return;
    finish();
  };

  panel.addEventListener('transitionend', onTransitionEnd);
  wrap._closeTimer = () => finish();
  setTimeout(finish, POPOVER_ANIMATION_MS + 80);
};

const closeAllDropdowns = (root, options = {}) => {
  root._popoverCleanup?.();
  root._popoverCleanup = null;

  root.querySelectorAll('.js-search-dropdown').forEach((wrap) => {
    if (wrap.classList.contains('is-open') || wrap.classList.contains('is-closing')) {
      closeDropdown(root, wrap, options);
    } else {
      const panel = wrap.querySelector('.offer-search__popover');
      if (panel) panel.hidden = true;
    }
  });

  if (!root.querySelector('.js-search-dropdown.is-open, .js-search-dropdown.is-closing')) {
    syncSearchOpenState(root);
  }
};

const syncSearchOpenState = (root) => {
  const hasOpen = Boolean(
    root.querySelector('.js-search-dropdown.is-open, .js-search-dropdown.is-closing')
  );
  root.closest(
    '.home-hero--search, .offers-page-narty__hero, .offers-page-dorosli__hero, .offers-page-zagraniczne__search, .offers-page-zagraniczne__hero',
  )?.classList.toggle('is-search-active', hasOpen);
  document.body.classList.toggle('offer-search-open', hasOpen);
};

const getNavbarOffsetPx = () => {
  const header = document.querySelector('.header, .site-header, #masthead');
  const bottom = header?.getBoundingClientRect().bottom;
  if (Number.isFinite(bottom) && bottom > 0) return bottom;
  const raw = getComputedStyle(document.body).getPropertyValue('--sticky-offset').trim();
  const probed = Number.parseFloat(raw);
  if (Number.isFinite(probed) && probed > 0) return probed;
  return 72;
};

/** Po otwarciu pola — 20px luzu pod nabarem nad paskiem pól (hero + listing). */
const scrollOfferSearchIntoView = (root) => {
  if (!root) return;
  const bar = root.querySelector('.offer-search__fields') || root;
  const targetTop = getNavbarOffsetPx() + 20;
  const delta = bar.getBoundingClientRect().top - targetTop;
  if (Math.abs(delta) < 2) return;
  window.scrollBy({
    top: delta,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
};

const positionPopover = (wrap) => {
  const trigger = wrap.querySelector('.offer-search__field');
  const panel = wrap.querySelector('.offer-search__popover');
  if (!trigger || !panel) return;

  computePosition(trigger, panel, {
    // Zawsze pod polem (hero) — bez flip w górę.
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [
      offset(8),
      shift({ padding: 16 }),
      // Długa lista tylko na mobile — na desktopie panel rośnie z treścią (bez scrollbara).
      // Mobile: szerokość dropdownu = szerokość selecta/przycisku; max-height ≤ 500px.
      size({
        padding: 2,
        apply({ elements, rects }) {
          const isMobile = window.matchMedia('(max-width: 767px)').matches;
          if (!isMobile) {
            elements.floating.style.maxHeight = '';
            elements.floating.style.width = '';
            elements.floating.style.minWidth = '';
            elements.floating.style.maxWidth = '';
            return;
          }
          // Stały max 500px (inline wygrywa z CSS).
          const triggerWidth = Math.round(rects.reference.width);
          Object.assign(elements.floating.style, {
            maxHeight: '500px',
            width: `${triggerWidth}px`,
            minWidth: `${triggerWidth}px`,
            maxWidth: `${triggerWidth}px`,
          });
        },
      }),
    ],
  }).then(({ x, y }) => {
    Object.assign(panel.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  });
};

const isDropdownEnabled = (wrap) => wrap && !wrap.classList.contains('is-disabled');

const syncDropdownDisabledState = (root) => {
  root.querySelectorAll('.js-search-dropdown').forEach((wrap) => {
    const disabled = wrap.classList.contains('is-disabled');
    const trigger = wrap.querySelector('.offer-search__field');
    trigger?.setAttribute('aria-disabled', String(disabled));

    if (disabled && (wrap.classList.contains('is-open') || wrap.classList.contains('is-closing'))) {
      closeDropdown(root, wrap, { immediate: true });
    }
  });
};

const openDropdown = (root, wrap) => {
  if (!isDropdownEnabled(wrap)) return;

  const trigger = wrap.querySelector('.offer-search__field');
  const panel = wrap.querySelector('.offer-search__popover');

  wrap._closeTimer?.();
  wrap._closeTimer = null;
  wrap.classList.remove('is-closing');

  if (panel) {
    panel.hidden = false;
  }

  // Force reflow so the enter transition runs after un-hiding the panel.
  panel?.getBoundingClientRect();

  wrap.classList.add('is-open');
  trigger?.setAttribute('aria-expanded', 'true');

  // Lazy-load turnus presets only when date popover opens (keeps Szukaj / listing snappy).
  if (wrap.dataset.dropdown === 'date') {
    const slotsKey = getTurnusSlotsKey(root);
    if (wrap.dataset.slotsKey !== slotsKey) {
      delete wrap.dataset.slotsLoaded;
      loadTurnusSlots(root);
    } else {
      syncDatePresetActive(wrap);
      focusCalendarOnRelevantTerm(wrap);
    }
  }

  // Hero / listing: najpierw ustaw pasek pól pod nabe (20px luzu), potem pozycjonuj panel.
  scrollOfferSearchIntoView(root);

  if (panel) {
    positionPopover(wrap);
    root._popoverCleanup?.();
    root._popoverCleanup = autoUpdate(trigger, panel, () => positionPopover(wrap));
  }

  syncSearchOpenState(root);
};

const toggleDropdown = (root, wrap) => {
  if (wrap.classList.contains('is-disabled')) {
    closeAllDropdowns(root);
    scrollOfferSearchIntoView(root);
    showDisabledFieldHint(root, wrap);
    return;
  }

  hideFieldHint();
  const isOpen = wrap.classList.contains('is-open');
  closeAllDropdowns(root);
  if (!isOpen) openDropdown(root, wrap);
};

const isEventInsideRoot = (root, event) => {
  if (root.contains(event.target)) return true;
  return event.composedPath?.().includes(root) ?? false;
};

const isEventInsidePopover = (event) => (
  event.composedPath?.().some(
    (node) => node instanceof Element && node.classList?.contains('offer-search__popover')
  ) ?? false
);

const resolveFieldFromEvent = (root, event) => {
  const direct = event.target.closest('.offer-search__field');
  if (direct && root.contains(direct)) return direct;

  return document.elementsFromPoint(event.clientX, event.clientY)
    .map((el) => el.closest?.('.offer-search__field'))
    .find((field) => field && root.contains(field)) || null;
};

const updateFieldDisplay = (root, wrap) => {
  const type = wrap.dataset.dropdown;
  const valueEl = root.querySelector(`.js-search-value-${type}`);
  if (!valueEl) return;

  if (type === 'destination') valueEl.textContent = getDestinationLabel(wrap);
  if (type === 'type') valueEl.textContent = getTypeLabel(wrap);
  if (type === 'date') valueEl.textContent = getDateLabel(wrap);
  if (type === 'guests') valueEl.textContent = getGuestsLabel(wrap);
};

const parseSearchUi = (root) => {
  const raw = root?.getAttribute('data-search-ui');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn('[HSKI OfferSearch] invalid data-search-ui JSON', error);
    return null;
  }
};

const FIELD_HINT_DEFAULTS = {
  hint_need_destination: 'Najpierw wybierz kierunek w polu „Dokąd”.',
  hint_need_type: 'Najpierw wybierz typ oferty.',
  hint_need_type_results: 'Wybierz typ oferty, aby zobaczyć wyniki.',
  hint_need_date: 'Najpierw wybierz termin.',
  hint_dates_on_product: 'Termin i liczbę osób wybierasz po kliknięciu „Zobacz szczegóły”.',
  date_guests_on_product_value: 'po Zobacz szczegóły',
  type_foreign_hint: 'Podczas rezerwacji wybierzesz czy chcesz samo szkolenie czy szkolenie z hotelem i wyżywieniem',
  type_poland_hint: 'Wybierz typ szkolenia — termin i liczbę osób ustawisz na stronie oferty.',
  hint_filters_hotel: 'Filtry działają tylko dla ofert z hotelem. Najpierw wybierz „Wyjazd z hotelem”.',
  filters_unlocked: 'Filtry dodatkowe oferty hotelowej',
};

const getSearchLabels = (root) => ({
  ...FIELD_HINT_DEFAULTS,
  ...(parseSearchUi(root)?.labels || {}),
  ...(parseSearchUi(root)?.phrases || {}),
});

const calendarCopy = (root) => {
  const cal = parseSearchUi(root)?.calendar || {};
  const en = isUiEnglish();
  const monthsFallback = en
    ? MONTH_NAMES_EN
    : MONTH_NAMES_PL.map((name) => msgFromHski(name) || name);
  const genitiveFallback = en
    ? MONTHS_GENITIVE_EN
    : MONTHS_GENITIVE_PL.map((name) => msgFromHski(name) || name);
  let months = Array.isArray(cal.months) && cal.months.length === 12 ? [...cal.months] : monthsFallback;
  let monthsGenitive = Array.isArray(cal.months_genitive) && cal.months_genitive.length === 12
    ? [...cal.months_genitive]
    : genitiveFallback;
  // Prod can ship a full 12-length PL calendar in data-search-ui — replace on EN.
  if (en && months[0] === MONTH_NAMES_PL[0]) {
    months = MONTH_NAMES_EN;
  }
  if (en && monthsGenitive[0] === MONTHS_GENITIVE_PL[0]) {
    monthsGenitive = MONTHS_GENITIVE_EN;
  }
  return { months, monthsGenitive };
};

let fieldHintHideTimer = 0;
let fieldHintOutsideCloser = null;
let fieldHintReposition = null;

const hideFieldHint = () => {
  const hint = document.getElementById('offer-search-barhint');
  if (hint) hint.classList.remove('is-visible');
  if (fieldHintHideTimer) {
    window.clearTimeout(fieldHintHideTimer);
    fieldHintHideTimer = 0;
  }
  if (fieldHintOutsideCloser) {
    document.removeEventListener('click', fieldHintOutsideCloser, true);
    fieldHintOutsideCloser = null;
  }
  if (fieldHintReposition) {
    window.removeEventListener('scroll', fieldHintReposition, true);
    window.removeEventListener('resize', fieldHintReposition);
    fieldHintReposition = null;
  }
};

const positionFieldHint = (anchor, hint) => {
  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const width = hint.offsetWidth;
  let left = rect.left + rect.width / 2 - width / 2;
  left = Math.max(10, Math.min(left, viewportWidth - width - 10));
  hint.style.left = `${left}px`;
  hint.style.top = `${rect.bottom + 12}px`;
  const caret = hint.querySelector('.offer-search-barhint__caret');
  if (caret) {
    const caretX = (rect.left + rect.width / 2) - left;
    caret.style.left = `${Math.max(14, Math.min(caretX, width - 14))}px`;
  }
};

const showFieldHint = (anchor, message) => {
  if (!anchor || !message) return;

  let hint = document.getElementById('offer-search-barhint');
  if (!hint) {
    hint = document.createElement('div');
    hint.id = 'offer-search-barhint';
    hint.className = 'offer-search-barhint';
    hint.setAttribute('role', 'tooltip');
    hint.setAttribute('aria-live', 'polite');
    document.body.appendChild(hint);
  }

  hint.replaceChildren();
  const caret = document.createElement('span');
  caret.className = 'offer-search-barhint__caret';
  caret.setAttribute('aria-hidden', 'true');
  hint.append(caret, document.createTextNode(message));

  hint.style.visibility = 'hidden';
  hint.classList.remove('is-visible');
  positionFieldHint(anchor, hint);
  hint.style.visibility = 'visible';
  void hint.offsetWidth;
  hint.classList.add('is-visible');

  if (fieldHintHideTimer) window.clearTimeout(fieldHintHideTimer);
  fieldHintHideTimer = window.setTimeout(() => hideFieldHint(), 4000);

  if (fieldHintOutsideCloser) {
    document.removeEventListener('click', fieldHintOutsideCloser, true);
  }
  window.setTimeout(() => {
    fieldHintOutsideCloser = (event) => {
      if (anchor.contains(event.target) || hint.contains(event.target)) return;
      hideFieldHint();
    };
    document.addEventListener('click', fieldHintOutsideCloser, true);
  }, 0);

  if (fieldHintReposition) {
    window.removeEventListener('scroll', fieldHintReposition, true);
    window.removeEventListener('resize', fieldHintReposition);
  }
  fieldHintReposition = () => {
    if (!hint.classList.contains('is-visible')) return;
    positionFieldHint(anchor, hint);
  };
  window.addEventListener('scroll', fieldHintReposition, true);
  window.addEventListener('resize', fieldHintReposition);
};

const getDisabledFieldHint = (root, wrap) => {
  const kind = wrap?.dataset.dropdown;
  const labels = getSearchLabels(root);
  const destinations = getSelectedDestinations(root);
  const mode = getDestinationMode(destinations);
  const hasDestination = destinations.length > 0 && mode !== 'invalid';
  const types = getSelectedTypes(root);
  const showDateGuests = shouldShowDateGuests(mode, types);

  if (!hasDestination) return labels.hint_need_destination;
  // PL / zagranica bez typu — Kiedy/Osoby zablokowane do wyboru Typu („Dowolna oferta” nie wystarczy).
  if ((kind === 'date' || kind === 'guests') && shouldRequireOfferType(mode, types)) {
    return labels.hint_need_type;
  }
  if ((kind === 'date' || kind === 'guests') && !showDateGuests) {
    return labels.hint_dates_on_product;
  }
  return labels.hint_need_destination;
};

const showDisabledFieldHint = (root, wrap) => {
  const trigger = wrap?.querySelector('.offer-search__field');
  showFieldHint(trigger, getDisabledFieldHint(root, wrap));
};

const getSearchUrls = (root = null) => {
  const ui = root ? parseSearchUi(root) : null;
  const fromCatalog = getSearchCatalogUrls();
  const urls = ui?.urls || {};

  return {
    children: urls.lista_dzieci || window.hskiSearch?.schoolChildrenUrl || fromCatalog.children,
    adults: urls.lista_dorosli || window.hskiSearch?.schoolAdultsUrl || fromCatalog.adults,
    travel: urls.oferta || window.hskiSearch?.travelUrl || fromCatalog.travel,
    oferty: window.hskiSearch?.ofertyUrl || urls.oferty || fromCatalog.oferty || '/oferty/',
    lessonProduct: urls.lekcje || window.hskiSearch?.lessonProductUrl || fromCatalog.lessonProduct,
    lessonSnowboardProduct: urls.lekcje_snowboard
      || window.hskiSearch?.lessonSnowboardProductUrl
      || fromCatalog.lessonSnowboardProduct,
    discipline: urls.discipline || fromCatalog.discipline || '',
    pingwin: urls.klub_pingwina || fromCatalog.pingwin,
    adultsCourses: urls.kursy_dorosli || fromCatalog.adultsCourses,
    contact: urls.kontakt || fromCatalog.contact,
  };
};

const tryNavigateFromSearch = (root, { decision: presetDecision, params: presetParams } = {}) => {
  const form = root.querySelector('.js-offer-search-form') || root;
  const params = presetParams || collectFromSearchRoot(form);
  const decision = presetDecision || resolveFormAction(
    params.destinations,
    params.types,
    getSearchUrls(root),
  );

  if (!decision.valid || !decision.action || isListingSearchAction(decision.action, getSearchUrls(root))) {
    return false;
  }

  const wireParams = {
    ...params,
    intent: decision.intent,
    types: decision.types,
    packageOnly: decision.flags.packageOnly,
    trainingOnly: decision.flags.trainingOnly,
  };

  navigateFromSearchDecision(decision, wireParams, getSearchUrls(root));
  return true;
};

const clearOtherCountries = (wrap, activeParent) => {
  wrap.querySelectorAll('.js-dest-parent').forEach((parent) => {
    if (parent.value === activeParent) return;
    parent.checked = false;
    parent.indeterminate = false;
    wrap.querySelectorAll(`.js-dest-child[data-parent="${parent.value}"]`).forEach((child) => {
      child.checked = false;
    });
  });
};

const getFullySelectedCountries = (wrap) => (
  [...wrap.querySelectorAll('.js-dest-parent')]
    .filter((item) => item.checked && !item.indeterminate)
);

const getFullySelectedCountry = (wrap) => getFullySelectedCountries(wrap)[0] || null;

const getSelectedDestinations = (root) => {
  const destinationWrap = root.querySelector('[data-dropdown="destination"]');
  if (!destinationWrap) return [];

  // Kraje zaznaczone w całości + ośrodki z krajów tylko częściowo zaznaczonych.
  const parents = [...destinationWrap.querySelectorAll('.js-dest-parent')];
  const fullCountries = getFullySelectedCountries(destinationWrap).map((item) => item.value);
  const childDestinations = [...destinationWrap.querySelectorAll('.js-dest-child:checked')]
    .filter((el) => {
      const parent = parents.find((item) => item.value === el.dataset.parent);
      return !(parent?.checked && !parent.indeterminate);
    })
    .map((el) => el.value);

  return [...fullCountries, ...childDestinations];
};

const clearDateState = (dateWrap, { syncRules = false } = {}) => {
  if (!dateWrap?._dateState) return;

  dateWrap._dateState.rangeStart = null;
  dateWrap._dateState.rangeEnd = null;
  dateWrap.querySelectorAll('.js-date-preset').forEach((preset) => {
    preset.classList.remove('is-active');
  });
  updateDateSummary(dateWrap, dateWrap._dateState, { syncRules });
  dateWrap._renderCalendar?.();
};

const clearGuestsState = (guestsWrap, root) => {
  if (!guestsWrap?._guestsState) return;

  guestsWrap._guestsState.adults = 2;
  guestsWrap._guestsState.children = 0;
  guestsWrap._pendingChildAges = [];

  const adultsEl = guestsWrap.querySelector('.js-guests-adults');
  const childrenEl = guestsWrap.querySelector('.js-guests-children');
  if (adultsEl) {
    adultsEl.textContent = '2';
    adultsEl.dataset.value = '2';
  }
  if (childrenEl) {
    childrenEl.textContent = '0';
    childrenEl.dataset.value = '0';
  }

  const adultsMinus = guestsWrap.querySelector('.js-guests-adults-minus');
  const childrenMinus = guestsWrap.querySelector('.js-guests-children-minus');
  if (adultsMinus) adultsMinus.disabled = false;
  if (childrenMinus) childrenMinus.disabled = true;

  renderChildAgeFields(guestsWrap, 0);
  if (root) updateFieldDisplay(root, guestsWrap);
};

/** Clear checked offer types (I3 — destination change). */
const clearTypesState = (root) => {
  const typeWrap = root.querySelector('[data-dropdown="type"]');
  if (!typeWrap) return;
  typeWrap.querySelectorAll('.js-type-option:checked').forEach((option) => {
    option.checked = false;
  });
  updateFieldDisplay(root, typeWrap);
};

const clearQuickFilters = (root) => {
  const scopes = [
    root,
    root.closest('.js-offer-search') || root,
    document,
  ];
  const seen = new Set();
  scopes.forEach((scope) => {
    scope.querySelectorAll(
      '.js-quick-meals, .js-quick-all-inclusive, .js-quick-training, .js-quick-transport',
    ).forEach((input) => {
      if (!(input instanceof HTMLInputElement) || seen.has(input)) return;
      seen.add(input);
      input.checked = false;
    });
  });
};

const clearAdvancedFilters = (root) => {
  const searchRoot = root.closest('.js-offer-search') || root;
  searchRoot.querySelectorAll('.js-filter-chip').forEach((chip) => {
    chip.classList.remove('is-on');
    chip.setAttribute('aria-pressed', 'false');
  });
  updateFiltersBadge(searchRoot);
};

const countAdvancedFilters = (root) => {
  const searchRoot = root.closest('.js-offer-search') || root;
  return searchRoot.querySelectorAll('.js-filter-chip.is-on, .js-filter-chip[aria-pressed="true"]').length;
};

const updateFiltersBadge = (root) => {
  const searchRoot = root.closest('.js-offer-search') || root;
  const badge = searchRoot.querySelector('.js-filters-badge');
  if (!badge) return;
  const count = countAdvancedFilters(searchRoot);
  badge.textContent = String(count);
  badge.hidden = count === 0;
};

const isHotelFiltersUnlocked = (root) => {
  const types = getSelectedTypes(root);
  return types.includes(HOTEL_TYPE_SLUG);
};

const setFiltersPanelOpen = (root, open) => {
  const searchRoot = root.closest('.js-offer-search') || root;
  const panel = searchRoot.querySelector('.js-filters-panel');
  const btn = searchRoot.querySelector('.js-filters-toggle');
  if (!panel || !btn) return;
  panel.hidden = !open;
  btn.classList.toggle('is-open', open);
  btn.setAttribute('aria-expanded', String(open));
};

const syncFiltersPanelLock = (root) => {
  const searchRoot = root.closest('.js-offer-search') || root;
  const btn = searchRoot.querySelector('.js-filters-toggle');
  const panel = searchRoot.querySelector('.js-filters-panel');
  if (!btn) return;

  const unlocked = isHotelFiltersUnlocked(searchRoot);
  const labels = getSearchLabels(searchRoot);
  btn.classList.toggle('is-locked', !unlocked);
  btn.removeAttribute('title');
  btn.setAttribute(
    'aria-label',
    unlocked ? labels.filters_unlocked : labels.hint_filters_hotel,
  );

  if (!unlocked) {
    if (panel && !panel.hidden) setFiltersPanelOpen(searchRoot, false);
    if (countAdvancedFilters(searchRoot) > 0) {
      clearAdvancedFilters(searchRoot);
    }
  }
  updateFiltersBadge(searchRoot);
};

const showFiltersLockHint = (btn, root) => {
  if (!btn) return;
  const prev = btn.getAttribute('data-hint-timer');
  if (prev) window.clearTimeout(Number(prev));
  btn.classList.add('is-hint');
  const timer = window.setTimeout(() => btn.classList.remove('is-hint'), 450);
  btn.setAttribute('data-hint-timer', String(timer));
  showFieldHint(btn, getSearchLabels(root || btn.closest('.js-offer-search')).hint_filters_hotel);
};

/** I3 — radical reset of downstream fields when destination/type changes. */
const resetDownstreamFields = (root, { clearTypes = false } = {}) => {
  if (clearTypes) {
    clearTypesState(root);
  }

  const dateWrap = root.querySelector('[data-dropdown="date"]');
  const guestsWrap = root.querySelector('[data-dropdown="guests"]');
  if (dateWrap) {
    delete dateWrap.dataset.slotsLoaded;
    delete dateWrap.dataset.slotsKey;
  }
  clearDateState(dateWrap, { syncRules: false });
  clearGuestsState(guestsWrap, root);
};

/** Destination change: wipe typ / kiedy / osoby / quick filters (PL↔zagranica). */
const resetAfterDestinationChange = (root) => {
  resetDownstreamFields(root, { clearTypes: true });
  clearQuickFilters(root);
  clearAdvancedFilters(root);
};

const getSelectedTypes = (root) => {
  const typeWrap = root.querySelector('[data-dropdown="type"]');
  return [...(typeWrap?.querySelectorAll('.js-type-option:checked') || [])].map((el) => el.value);
};

const SCHOOL_TYPE_SECTION_AUDIENCE = {
  children: 'children',
  adults: 'adults',
  Dzieci: 'children',
  Dorośli: 'adults',
  'Dla dzieci': 'children',
  'Dla dorosłych': 'adults',
  Children: 'children',
  Adults: 'adults',
  'For children': 'children',
  'For adults': 'adults',
  Instruktorskie: 'instructor',
  Instructors: 'instructor',
};

const applySchoolAudienceRules = (root, typeWrap) => {
  if (!root.classList.contains('offer-search--school') || !typeWrap) return;

  const audience = root.closest('.js-offers-list-school')?.dataset.schoolAudience
    || root.dataset.schoolAudience;
  if (!audience) return;

  typeWrap.querySelectorAll('.offer-search__type-section').forEach((section) => {
    const heading = section.querySelector('.offer-search__type-heading-label')?.textContent?.trim()
      || section.querySelector('.offer-search__type-heading')?.textContent?.trim();
    const sectionAudience = SCHOOL_TYPE_SECTION_AUDIENCE[section.dataset.group]
      || SCHOOL_TYPE_SECTION_AUDIENCE[heading];
    const disabled = (
      (audience === 'children' && sectionAudience === 'adults')
      || (audience === 'adults' && sectionAudience === 'children')
    );

    section.classList.toggle('is-section-disabled', disabled);
    if (disabled) {
      section.querySelectorAll('.js-type-option:checked').forEach((option) => {
        option.checked = false;
      });
    }
  });
};

const refreshSearchRules = (root, { allowReset = true } = {}) => {
  const destinationWrap = root.querySelector('[data-dropdown="destination"]');
  const typeWrap = root.querySelector('[data-dropdown="type"]');
  const dateWrap = root.querySelector('[data-dropdown="date"]');
  const typeDropdown = root.querySelector('.js-search-dropdown[data-dropdown="type"]');
  const dateDropdown = root.querySelector('.js-search-dropdown[data-dropdown="date"]');
  const guestsDropdown = root.querySelector('.js-search-dropdown[data-dropdown="guests"]');
  const destinations = getSelectedDestinations(root);
  const previousSelection = root._searchSelection || null;
  let types = getSelectedTypes(root);

  // I3: destination change clears typ + kiedy + osoby before intent resolution.
  // Date/guests updates must not reset — picking a hotel week can retarget Dokąd
  // (same country, other resort) and that is not a user dest/type change.
  const nextDestKey = captureSelection(destinations, types).destinationKey;
  const destinationChanged = Boolean(previousSelection)
    && previousSelection.destinationKey !== nextDestKey;
  if (allowReset && destinationChanged) {
    resetAfterDestinationChange(root);
    types = [];
  }

  const mode = getDestinationMode(destinations);
  const context = getSearchContext(destinations, { types });
  const visible = getVisibleTypeSlugs(context);

  if (typeWrap) {
    typeWrap.querySelectorAll('.js-type-option').forEach((option) => {
      const row = option.closest('.offer-search__type-item');
      const show = context.showTypes && visible.has(option.value);
      if (row) row.hidden = !show;
      if (!show && allowReset) option.checked = false;
    });

    typeWrap.querySelectorAll('.offer-search__type-section').forEach((section) => {
      const hasVisible = [...section.querySelectorAll('.offer-search__type-item')].some((row) => !row.hidden);
      section.hidden = !context.showTypes || !hasVisible;
    });

    typeWrap.querySelectorAll('.js-type-school-block').forEach((block) => {
      const hasVisible = [...block.querySelectorAll('.offer-search__type-section')].some((section) => !section.hidden);
      block.hidden = !hasVisible;
    });

    const typeHint = typeWrap.querySelector('.js-type-mode-hint');
    if (typeHint) {
      const labels = getSearchLabels(root);
      const defaultHint = typeHint.dataset.defaultHint || labels.type_popover_desc || '';
      const foreignHint = typeHint.dataset.foreignHint || labels.type_popover_desc || defaultHint;
      const polandHint = typeHint.dataset.polandHint || labels.type_poland_hint || defaultHint;
      if (mode === 'foreign' && foreignHint) {
        typeHint.textContent = foreignHint;
      } else if (mode === 'poland' && polandHint) {
        typeHint.textContent = polandHint;
      } else {
        typeHint.textContent = defaultHint;
      }
      typeHint.hidden = !typeHint.textContent;
    }

    applySchoolAudienceRules(root, typeWrap);
  }

  types = getSelectedTypes(root);
  const { decision, selection, resetDownstream } = transitionSearchRules(
    previousSelection,
    destinations,
    types,
    getSearchUrls(root),
  );
  root._searchSelection = selection;

  // I3: clear date/guests when type changed (destination already fully reset above).
  if (allowReset && resetDownstream && !destinationChanged) {
    resetDownstreamFields(root);
  }

  const hasDestination = destinations.length > 0 && mode !== 'invalid';
  const showDateGuests = decision.showDateGuests ?? shouldShowDateGuests(mode, types);

  typeDropdown?.classList.toggle('is-disabled', !hasDestination);
  // Spec renderBar: Typ po Dokąd; Kiedy gdy inventarz (wyjazd-hotel) po wyborze typu.
  // Ile osób? odblokowane wraz z Kiedy — skład nie zależy od wybranego terminu.
  // Bez typu (Dowolna oferta) oraz szkolenie: Kiedy/Osoby zablokowane (showDateGuests = false).
  dateDropdown?.classList.toggle('is-disabled', !hasDestination || !showDateGuests);
  guestsDropdown?.classList.toggle(
    'is-disabled',
    !hasDestination || !showDateGuests,
  );
  dateDropdown?.classList.remove('is-field-hidden');
  guestsDropdown?.classList.remove('is-field-hidden');
  if (dateDropdown) dateDropdown.hidden = false;
  if (guestsDropdown) guestsDropdown.hidden = false;
  const dateLegend = dateDropdown?.querySelector('.js-date-legend');
  if (dateLegend) dateLegend.hidden = !isHotelDateSearch(root);

  if (allowReset && !showDateGuests) {
    clearDateState(dateWrap, { syncRules: false });
    clearGuestsState(guestsDropdown, root);
  }
  // Do not fetch search-presets here — only when the date popover opens
  // (openDropdown). Eager load put presets on the homepage critical path.

  syncDropdownDisabledState(root);

  if (dateWrap?._dateState) {
    dateWrap._dateState.allowSingleDay = allowsSingleDaySelection(types);
  }

  const form = root.querySelector('.js-offer-search-form');
  if (form) {
    if (decision.action) form.setAttribute('action', decision.action);
    else form.removeAttribute('action');

    const submitBtn = form.querySelector('[type="submit"], .js-offer-search-submit');
    if (submitBtn) {
      // Spec szukaj(): data/osoby nie blokują. PL/zagranica bez typu — hint na Typ.
      const canSubmit = decision.valid && !shouldRequireOfferType(mode, types);
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.toggle('is-incomplete', !canSubmit);
    }
  }

  if (destinationWrap) updateFieldDisplay(root, destinationWrap);
  if (typeWrap) updateFieldDisplay(root, typeWrap);
  syncDateGuestsFieldHints(root, {
    showDateGuests,
    hasDestination,
    hasTypes: types.length > 0,
  });
  syncFiltersPanelLock(root);
};

/** Mock renderBar: PL + szkolenie → w polach Kiedy/Osoby „po Zobacz szczegóły”. */
const syncDateGuestsFieldHints = (root, { showDateGuests, hasDestination, hasTypes }) => {
  const dateWrap = root.querySelector('[data-dropdown="date"]');
  const guestsWrap = root.querySelector('[data-dropdown="guests"]');
  const dateValue = root.querySelector('.js-search-value-date');
  const guestsValue = root.querySelector('.js-search-value-guests');
  const labels = getSearchLabels(root);
  const onProductValue = labels.date_guests_on_product_value || 'po Zobacz szczegóły';

  if (!showDateGuests && hasDestination && hasTypes) {
    if (dateValue) dateValue.textContent = onProductValue;
    if (guestsValue) guestsValue.textContent = onProductValue;
    return;
  }

  if (dateWrap) updateFieldDisplay(root, dateWrap);
  if (guestsWrap) updateFieldDisplay(root, guestsWrap);
};

/**
 * Listen for destination/type (and quick filters) → refreshSearchRules.
 * Downstream reset runs inside refreshSearchRules only when selection changes.
 */
export const bindEvents = (root) => {
  if (!root || root.dataset.searchRulesBound === '1') return;
  root.dataset.searchRulesBound = '1';

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const isUpstream = target.matches(
      '.js-dest-parent, .js-dest-child, .js-type-option',
    );
    const isQuickFilter = target.matches(
      '.js-quick-meals, .js-quick-all-inclusive, .js-quick-training, .js-quick-transport',
    );
    if (!isUpstream && !isQuickFilter) return;

    if (isUpstream) {
      refreshSearchRules(root);
    }
    if (root.dataset.searchReady === '1' && isUnifiedListingContext()) {
      if (target.matches('.js-type-option')) {
        const types = getSelectedTypes(root);
        if (types.length && !types.includes(HOTEL_TYPE_SLUG)) {
          return;
        }
      }
      root.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
    }
  });

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggleBtn = target.closest('.js-filters-toggle');
    if (toggleBtn && root.contains(toggleBtn)) {
      event.preventDefault();
      if (!isHotelFiltersUnlocked(root)) {
        showFiltersLockHint(toggleBtn, root);
        return;
      }
      const panel = root.querySelector('.js-filters-panel');
      setFiltersPanelOpen(root, Boolean(panel?.hidden));
      return;
    }

    const chip = target.closest('.js-filter-chip');
    if (chip && root.contains(chip)) {
      event.preventDefault();
      if (!isHotelFiltersUnlocked(root)) return;
      const on = !(chip.classList.contains('is-on') || chip.getAttribute('aria-pressed') === 'true');
      chip.classList.toggle('is-on', on);
      chip.setAttribute('aria-pressed', String(on));
      updateFiltersBadge(root);
      if (root.dataset.searchReady === '1' && isUnifiedListingContext()) {
        root.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
      }
      return;
    }

    if (target.closest('.js-filters-clear') && root.contains(target.closest('.js-filters-clear'))) {
      event.preventDefault();
      clearAdvancedFilters(root);
      if (root.dataset.searchReady === '1' && isUnifiedListingContext()) {
        root.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
      }
      return;
    }

    if (target.closest('.js-filters-apply') && root.contains(target.closest('.js-filters-apply'))) {
      event.preventDefault();
      setFiltersPanelOpen(root, false);
      const form = root.querySelector('.js-offer-search-form');
      if (form && !form.querySelector('[type="submit"]')?.disabled) {
        form.requestSubmit?.() || form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } else if (root.dataset.searchReady === '1' && isUnifiedListingContext()) {
        root.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
      }
    }
  });
};

const polishNightsLabel = (nights, labels = {}) => {
  if (nights === 1) return i18nOr(labels.night_one, '1 noc');
  if (nights >= 2 && nights <= 4) {
    return `${nights} ${i18nOr(labels.night_few, 'noce')}`;
  }
  return `${nights} ${i18nOr(labels.night_many, 'nocy')}`;
};

const padDay = (value) => String(value).padStart(2, '0');

const formatHotelTermTitle = (startIso, endIso, monthsGenitive = MONTHS_GENITIVE_PL) => {
  const start = parseDate(startIso);
  const end = parseDate(endIso);
  if (!start || !end) return `${startIso} → ${endIso}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const fallbackGenitive = MONTHS_GENITIVE_PL.map((name) => msgFromHski(name) || name);
  const monthName = (date) => (
    monthsGenitive[date.getMonth()] || fallbackGenitive[date.getMonth()]
  );
  if (sameMonth) {
    return `${padDay(start.getDate())} → ${padDay(end.getDate())} ${monthName(start)} ${start.getFullYear()}`;
  }
  if (sameYear) {
    return `${padDay(start.getDate())} ${monthName(start)} → ${padDay(end.getDate())} ${monthName(end)} ${start.getFullYear()}`;
  }
  return `${padDay(start.getDate())} ${monthName(start)} ${start.getFullYear()} → ${padDay(end.getDate())} ${monthName(end)} ${end.getFullYear()}`;
};

const formatIsoDate = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const monthKeyFromDate = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/** Jump dual calendar to selected range, or nearest upcoming turnus. */
const focusCalendarOnRelevantTerm = (dateWrap, { render = true } = {}) => {
  const state = dateWrap?._dateState;
  if (!state) return;

  let targetMonth = null;
  if (state.rangeStart) {
    targetMonth = monthKeyFromDate(state.rangeStart);
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = (dateWrap._turnusSlots || [])
      .map((slot) => ({ start: parseDate(slot.start) }))
      .filter((item) => item.start)
      .sort((a, b) => a.start - b.start);
    const nearest = upcoming.find((item) => item.start >= today) || upcoming[0];
    if (nearest) targetMonth = monthKeyFromDate(nearest.start);
  }

  if (targetMonth && state.viewMonth !== targetMonth) {
    state.viewMonth = targetMonth;
  }
  if (render) dateWrap._renderCalendar?.();
};

const syncDatePresetActive = (dateWrap) => {
  const presets = dateWrap?.querySelectorAll('.js-date-preset');
  if (!presets?.length) return;

  const state = dateWrap._dateState;
  if (!state?.rangeStart) {
    presets.forEach((preset) => preset.classList.remove('is-active'));
    return;
  }

  const startIso = formatIsoDate(state.rangeStart);
  const endIso = formatIsoDate(state.rangeEnd || state.rangeStart);
  let active = null;
  presets.forEach((preset) => {
    const match = formatIsoDate(parseDate(preset.dataset.start)) === startIso
      && formatIsoDate(parseDate(preset.dataset.end)) === endIso;
    preset.classList.toggle('is-active', match);
    if (match && !active) active = preset;
  });

  if (!active) return;

  const ferie = active.closest('.offer-search__ferie');
  if (ferie) {
    const toggle = ferie.querySelector('.js-ferie-toggle');
    const panel = ferie.querySelector('.js-ferie-panel');
    toggle?.classList.add('is-open');
    toggle?.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
  }

  const activeMonth = String(active.dataset.start || '').slice(0, 7);
  if (activeMonth && state.viewMonth !== activeMonth) {
    state.viewMonth = activeMonth;
    dateWrap._renderCalendar?.();
  }

  if (dateWrap.classList.contains('is-open')) {
    active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

const isHotelDateSearch = (root) => getSelectedTypes(root).includes(HOTEL_TYPE_SLUG);

const getDestinationsForHotelPresets = (root) => getSelectedDestinations(root);

const slotsForSelectedDestination = (root, slots = []) => {
  if (!Array.isArray(slots) || !slots.length || !isHotelDateSearch(root)) return slots;
  const dests = getSelectedDestinations(root);
  if (!dests.length) return slots;
  const wrap = root.querySelector('[data-dropdown="destination"]');
  const selected = new Set(dests);
  const hasFullCountry = dests.some((dest) => (
    Boolean(wrap?.querySelector(`.js-dest-parent[value="${dest}"]`))
    && !wrap?.querySelector(`.js-dest-child[value="${dest}"]`)
  ));
  if (hasFullCountry) return slots;
  return slots.filter((slot) => !slot.dest || selected.has(slot.dest));
};

const getActiveSeason = (root) => {
  const seasonBtn = document.querySelector('.js-season-tabs .offer-search__season-btn.is-active')
    || root.querySelector('.offer-search__season-btn.is-active');
  return seasonBtn?.dataset.season || 'winter';
};

const getTurnusSlotsKey = (root) => (
  `${getActiveSeason(root)}|${getDestinationsForHotelPresets(root).join(',')}|${getSelectedTypes(root).join(',')}`
);

const turnusLoadSeq = new WeakMap();

const loadTurnusSlots = async (root) => {
  const dateWrap = root.querySelector('[data-dropdown="date"]');
  if (!dateWrap) return;

  const seq = (turnusLoadSeq.get(root) || 0) + 1;
  turnusLoadSeq.set(root, seq);
  const slotsKey = getTurnusSlotsKey(root);
  const isCurrent = () => turnusLoadSeq.get(root) === seq && getTurnusSlotsKey(root) === slotsKey;

  const isSchool = root.classList.contains('offer-search--school');
  const hotel = isHotelDateSearch(root);
  // Travel: keep SSR/twig presets until API returns. School: merge defaults.
  if (!dateWrap._turnusSlots) {
    dateWrap._turnusSlots = isSchool && !hotel ? mergeTurnusSlots() : [];
  }

  const presetsUrl = window.hskiSearch?.presetsUrl;
  if (!presetsUrl) {
    if (isCurrent()) focusCalendarOnRelevantTerm(dateWrap);
    return;
  }

  try {
    const url = new URL(presetsUrl, window.location.origin);
    const destinations = getDestinationsForHotelPresets(root);
    const types = getSelectedTypes(root);
    url.searchParams.set('season', getActiveSeason(root));
    destinations.forEach((slug) => {
      url.searchParams.append('destination[]', slug);
    });
    if (destinations[0]) {
      url.searchParams.set('destination', destinations[0]);
    }
    types.forEach((slug) => {
      url.searchParams.append('type[]', slug);
      url.searchParams.set('type', slug);
    });
    if (hotel) url.searchParams.set('package_only', '1');

    const response = await fetch(url.toString());
    if (!isCurrent()) return;
    if (!response.ok) return;
    const payload = await response.json();
    if (!isCurrent()) return;
    const fromApi = Array.isArray(payload.turnus_slots) ? payload.turnus_slots : [];

    dateWrap._turnusSlots = hotel
      ? slotsForSelectedDestination(root, fromApi)
      : (isSchool
        ? mergeTurnusSlots(payload.school_turnus_slots || fromApi, { includeDefaults: true })
        : mergeTurnusSlots(fromApi, { includeDefaults: false }));

    if (hotel || !isSchool) {
      syncTravelDatePresets(dateWrap, dateWrap._turnusSlots, { hotel });
    } else {
      focusCalendarOnRelevantTerm(dateWrap);
    }

    dateWrap.dataset.slotsLoaded = '1';
    dateWrap.dataset.slotsKey = slotsKey;
  } catch {
    // Offline: keep SSR presets / school defaults.
  }
};

const daysBetween = (startIso, endIso) => {
  const start = parseDate(startIso);
  const end = parseDate(endIso);
  if (!start || !end || end < start) return 1;
  return Math.max(1, Math.round((end - start) / 86400000));
};

const polishDaysLabel = (days, labels = {}) => {
  if (days === 1) return i18nOr(labels.day_one, '1 dzień');
  return `${days} ${i18nOr(labels.day_many, 'dni')}`;
};

const syncTravelDatePresets = (dateWrap, slots = [], { hotel = false } = {}) => {
  const list = dateWrap.querySelector('.offer-search__date-presets');
  if (!list) return;

  list.querySelectorAll('.js-date-preset').forEach((btn) => {
    if (btn.closest('.offer-search__ferie')) return;
    btn.remove();
  });

  const ferie = list.querySelector('.offer-search__ferie');
  if (ferie) ferie.hidden = hotel;

  list.querySelector('.js-date-empty')?.remove();

  if (!slots.length) {
    if (hotel) {
      const empty = document.createElement('p');
      empty.className = 'offer-search__date-empty js-date-empty';
      empty.textContent = i18nOr(
        getSearchLabels(dateWrap.closest('.js-offer-search')).date_empty,
        'Brak terminów dla tego kierunku.',
      );
      if (ferie) list.insertBefore(empty, ferie);
      else list.prepend(empty);
    }
    return;
  }

  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const searchRoot = dateWrap.closest('.js-offer-search');
  const phrases = getSearchLabels(searchRoot);
  const monthsGenitive = calendarCopy(searchRoot).monthsGenitive;
  const fragment = document.createDocumentFragment();
  slots.slice(0, 16).forEach((slot) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'offer-search__preset js-date-preset';
    button.dataset.start = slot.start;
    button.dataset.end = slot.end;
    const days = Number(slot.days) > 0 ? Number(slot.days) : daysBetween(slot.start, slot.end);
    const nights = Number(slot.nights) > 0 ? Number(slot.nights) : days;
    const isHotelSlot = hotel || slot.kind === 'hotel';
    const termTitle = formatHotelTermTitle(slot.start, slot.end, monthsGenitive);
    const customTitle = (slot.title || '').trim();
    // Always use a prominent title chip (strong) — never a lone 11px span.
    const title = customTitle || termTitle;
    const subtitle = isHotelSlot ? polishNightsLabel(nights, phrases) : polishDaysLabel(days, phrases);
    button.dataset.label = title;
    button.dataset.title = title;
    button.innerHTML = `<strong>${escapeHtml(title)}</strong><small>${escapeHtml(subtitle)}</small>`;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const state = dateWrap._dateState;
      if (!state) return;
      state.rangeStart = parseDate(slot.start);
      state.rangeEnd = parseDate(slot.end);
      state.viewMonth = slot.start.slice(0, 7);
      const searchRoot = dateWrap.closest('.js-offer-search');
      updateDateSummary(dateWrap, state, { syncRules: false });
      dateWrap._renderCalendar?.();
      refreshSearchRules(searchRoot, { allowReset: false });
    });
    fragment.appendChild(button);
  });

  if (ferie) list.insertBefore(fragment, ferie);
  else list.prepend(fragment);

  syncDatePresetActive(dateWrap);
  focusCalendarOnRelevantTerm(dateWrap);
};

const initDestinationDropdown = (wrap) => {
  const parents = wrap.querySelectorAll('.js-dest-parent');
  const children = wrap.querySelectorAll('.js-dest-child');

  parents.forEach((parent) => {
    parent.addEventListener('change', () => {
      const parentValue = parent.value;

      if (parent.checked) {
        clearOtherCountries(wrap, parentValue);
        // Kraj = wszystkie ośrodki w tym kraju (UI + wire: slug kraju).
        wrap.querySelectorAll(`.js-dest-child[data-parent="${parentValue}"]`).forEach((child) => {
          child.checked = true;
        });
        parent.indeterminate = false;
      } else {
        wrap.querySelectorAll(`.js-dest-child[data-parent="${parentValue}"]`).forEach((child) => {
          child.checked = false;
        });
      }
    });
  });

  children.forEach((child) => {
    child.addEventListener('change', () => {
      const parentValue = child.dataset.parent;
      clearOtherCountries(wrap, parentValue);

      // Multi-select ośrodków w ramach jednego kraju (inne kraje czyści clearOtherCountries).
      const parent = wrap.querySelector(`.js-dest-parent[value="${parentValue}"]`);
      const siblings = wrap.querySelectorAll(`.js-dest-child[data-parent="${parentValue}"]`);
      if (parent) {
        parent.checked = [...siblings].every((item) => item.checked);
        parent.indeterminate = !parent.checked && [...siblings].some((item) => item.checked);
      }
    });
  });

  wrap.querySelectorAll('.js-dest-parent').forEach((parent) => {
    const siblings = wrap.querySelectorAll(`.js-dest-child[data-parent="${parent.value}"]`);
    if (!siblings.length) return;
    parent.checked = [...siblings].every((item) => item.checked);
    parent.indeterminate = !parent.checked && [...siblings].some((item) => item.checked);
  });
};

const initTypeDropdown = (wrap) => {
  wrap.querySelectorAll('.js-type-option').forEach((option) => {
    option.addEventListener('change', () => {
      if (option.checked) {
        wrap.querySelectorAll('.js-type-option').forEach((other) => {
          if (other !== option) other.checked = false;
        });
      }
    });
  });
};

const isSchoolSearch = (root) => (
  root?.dataset.searchVariant === 'school' || root?.classList.contains('offer-search--school')
);

const emptyDestinationLabel = (wrap) => {
  const root = wrap.closest('.js-offer-search');
  const labels = getSearchLabels(root);
  if (isSchoolSearch(root)) {
    return labels.destination_placeholder_school || 'Zakopane (Harenda)';
  }
  return labels.destination_placeholder || '— wybierz kierunek —';
};

const emptyTypeLabel = (wrap) => {
  const root = wrap.closest('.js-offer-search');
  const labels = getSearchLabels(root);
  if (isSchoolSearch(root)) {
    return labels.type_placeholder_school || 'Dzieci / Klub Pingwina';
  }
  return labels.type_placeholder || 'Dowolna oferta';
};

const getDestinationLabel = (wrap) => {
  const parent = getFullySelectedCountry(wrap);
  if (parent) {
    const name = parent.dataset.label || parent.value;
    const hasChildren = wrap.querySelector(`.js-dest-child[data-parent="${parent.value}"]`);
    if (!hasChildren) return name;
    const allResorts = i18nOr(
      getSearchLabels(wrap.closest('.js-offer-search')).destination_all_resorts,
      'wszystkie ośrodki',
    );
    // Chip must never keep PL “wszystkie ośrodki” on EN even if label value is custom-but-Polish.
    const resortsLabel = isUiEnglish() && /ośrodki/i.test(allResorts)
      ? (PHRASE_EN['wszystkie ośrodki'] || 'All resorts')
      : allResorts;
    return `${name}, ${resortsLabel}`;
  }

  const selectedChildren = [...wrap.querySelectorAll('.js-dest-child:checked')];
  if (!selectedChildren.length) return emptyDestinationLabel(wrap);

  const labels = selectedChildren.map((item) => item.dataset.label || item.value);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(', ');
  return `${labels[0]} +${labels.length - 1}`;
};

const getTypeLabel = (wrap) => {
  const selected = [...wrap.querySelectorAll('.js-type-option:checked')];
  if (!selected.length) return emptyTypeLabel(wrap);
  return selected.map((item) => {
    const group = item.dataset.group;
    return group ? `${group} / ${item.dataset.label}` : item.dataset.label;
  }).join(', ');
};

const turnusSlotId = (slot) => (
  slot?.start && slot?.end ? `${slot.start}_${slot.end}` : ''
);

const escapeOfferSlotSelector = (slotId) => (
  typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(slotId)
    : String(slotId).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
);

const initCalendar = (wrap, state) => {
  const calendar = wrap.querySelector('.js-search-calendar');
  const grid = wrap.querySelector('.js-calendar-grid');
  const title = wrap.querySelector('.js-calendar-title');
  const extraGrid = wrap.querySelector('.js-calendar-grid-extra');
  const extraTitle = wrap.querySelector('.js-calendar-title-extra');
  const calendarRoot = wrap.querySelector('.offer-search__calendar-stack') || calendar?.parentElement;
  if (!calendar || !grid || !title) return null;

  const getSlots = () => {
    const searchRoot = wrap.closest('.js-offer-search');
    return searchRoot
      ? slotsForSelectedDestination(searchRoot, wrap._turnusSlots || [])
      : (wrap._turnusSlots || []);
  };

  const getTurnusTone = (date) => {
    const turnus = findTurnusForDate(date, getSlots());
    return turnus?.tone || '';
  };

  const isInSelectedRange = (date) => {
    if (!state.rangeStart) return false;
    const end = state.rangeEnd || state.rangeStart;
    const time = date.getTime();
    return time >= state.rangeStart.getTime() && time <= end.getTime();
  };

  const clearCalendarHover = () => {
    delete wrap.dataset.hoverSlotId;
    calendarRoot?.querySelectorAll('.offer-search__calendar-day--hover-range').forEach((el) => {
      el.classList.remove(
        'offer-search__calendar-day--hover-range',
        'offer-search__calendar-day--rs',
        'offer-search__calendar-day--re',
      );
    });
  };

  /** Preview full turnus week on hover (Pingwin-style), without changing selection. */
  const setCalendarHoverSlot = (slotId) => {
    if (!slotId || state.allowSingleDay) {
      clearCalendarHover();
      return;
    }
    if (wrap.dataset.hoverSlotId === slotId) return;
    wrap.dataset.hoverSlotId = slotId;
    if (!calendarRoot) return;
    calendarRoot.querySelectorAll('.offer-search__calendar-day--hover-range').forEach((el) => {
      el.classList.remove(
        'offer-search__calendar-day--hover-range',
        'offer-search__calendar-day--rs',
        'offer-search__calendar-day--re',
      );
    });
    const escaped = escapeOfferSlotSelector(slotId);
    const days = [...calendarRoot.querySelectorAll(
      `.offer-search__calendar-day[data-slot-id="${escaped}"]`,
    )];
    if (!days.length) return;
    const startIso = slotId.split('_')[0] || '';
    const endIso = slotId.split('_')[1] || '';
    days.forEach((el) => {
      el.classList.add('offer-search__calendar-day--hover-range');
      if (el.dataset.date === startIso) el.classList.add('offer-search__calendar-day--rs');
      if (el.dataset.date === endIso) el.classList.add('offer-search__calendar-day--re');
    });
  };

  const renderMonth = (targetGrid, targetTitle, monthKey, interactive = true) => {
    if (!targetGrid) return;

    const [year, month] = monthKey.split('-').map(Number);
    const monthLabel = calendarCopy(wrap.closest('.js-offer-search')).months[month - 1]
      || msgFromHski(MONTH_NAMES_PL[month - 1])
      || MONTH_NAMES_PL[month - 1];
    if (targetTitle) targetTitle.textContent = `${monthLabel} ${year}`;
    targetGrid.innerHTML = '';

    const firstDay = new Date(year, month - 1, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const prevMonthDays = new Date(year, month - 1, 0).getDate();
    const slots = getSlots();
    const rangeStartTs = state.rangeStart?.getTime() ?? null;
    const rangeEndTs = (state.rangeEnd || state.rangeStart)?.getTime() ?? null;
    const hasFullRange = rangeStartTs != null && rangeEndTs != null && state.rangeEnd;

    for (let i = 0; i < startOffset; i += 1) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'offer-search__calendar-day offer-search__calendar-day--muted';
      btn.textContent = String(prevMonthDays - startOffset + i + 1);
      btn.tabIndex = -1;
      targetGrid.appendChild(btn);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month - 1, day);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'offer-search__calendar-day';
      btn.textContent = String(day);
      btn.dataset.date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const turnus = findTurnusForDate(date, slots);
      const slotId = turnusSlotId(turnus);
      if (slotId) btn.dataset.slotId = slotId;

      const inSelected = isInSelectedRange(date);
      const tone = turnus?.tone || '';
      if (tone && tone !== 'default' && !inSelected) {
        btn.classList.add(`offer-search__calendar-day--tone-${tone}`);
      } else if (tone && !inSelected) {
        btn.classList.add('offer-search__calendar-day--turnus');
      }

      if (hasFullRange) {
        const time = date.getTime();
        // Entire selected week/term as one solid blue bar (not only endpoints).
        if (time > rangeStartTs && time < rangeEndTs) {
          btn.classList.add('offer-search__calendar-day--in-range');
        }
        if (time === rangeStartTs) btn.classList.add('offer-search__calendar-day--start');
        if (time === rangeEndTs) btn.classList.add('offer-search__calendar-day--end');
      } else if (state.rangeStart && date.getTime() === state.rangeStart.getTime()) {
        btn.classList.add('offer-search__calendar-day--selected');
      }

      if (interactive) {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          const clicked = parseDate(btn.dataset.date);
          const liveSlots = getSlots();
          const hit = findTurnusForDate(clicked, liveSlots);

          // Week/term snap for hotel & course calendars; single-day only for lessons.
          if (hit && !state.allowSingleDay) {
            state.rangeStart = parseDate(hit.start);
            state.rangeEnd = parseDate(hit.end);
          } else if (state.allowSingleDay) {
            state.rangeStart = clicked;
            state.rangeEnd = clicked;
          } else if (hit) {
            state.rangeStart = parseDate(hit.start);
            state.rangeEnd = parseDate(hit.end);
          } else if (!state.rangeStart || (state.rangeStart && state.rangeEnd)) {
            state.rangeStart = clicked;
            state.rangeEnd = null;
          } else if (clicked < state.rangeStart) {
            state.rangeEnd = state.rangeStart;
            state.rangeStart = clicked;
          } else {
            state.rangeEnd = clicked;
          }

          clearCalendarHover();
          updateDateSummary(wrap, state, { syncRules: false });
          render();
          refreshSearchRules(wrap.closest('.js-offer-search'), { allowReset: false });
        });
      } else {
        btn.tabIndex = -1;
      }

      targetGrid.appendChild(btn);
    }
  };

  const getExtraMonthKey = (monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    const date = new Date(year, month, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const render = () => {
    clearCalendarHover();
    renderMonth(grid, title, state.viewMonth, true);
    if (extraGrid) {
      // Second month must be selectable (Figma dual calendar).
      renderMonth(extraGrid, extraTitle, getExtraMonthKey(state.viewMonth), true);
    }
  };

  if (!wrap.dataset.calendarBound) {
    wrap.dataset.calendarBound = '1';
    wrap.querySelector('.js-calendar-prev')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const [year, month] = state.viewMonth.split('-').map(Number);
      const date = new Date(year, month - 2, 1);
      state.viewMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      render();
    });

    wrap.querySelector('.js-calendar-next')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const [year, month] = state.viewMonth.split('-').map(Number);
      const date = new Date(year, month, 1);
      state.viewMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      render();
    });

    // Hover any day of a turnus → preview the full week bar.
    calendarRoot?.addEventListener('pointerover', (event) => {
      const day = event.target.closest?.('.offer-search__calendar-day[data-slot-id]');
      if (!day || !calendarRoot.contains(day)) return;
      setCalendarHoverSlot(day.getAttribute('data-slot-id') || '');
    });
    calendarRoot?.addEventListener('pointerleave', clearCalendarHover);

    // Hover a preset in the side list → same week preview in the calendar.
    wrap.querySelector('.offer-search__date-presets')?.addEventListener('pointerover', (event) => {
      const preset = event.target.closest?.('.js-date-preset');
      if (!preset || !wrap.contains(preset)) return;
      const start = preset.dataset.start;
      const end = preset.dataset.end;
      if (start && end) setCalendarHoverSlot(`${start}_${end}`);
    });
    wrap.querySelector('.offer-search__date-presets')?.addEventListener('pointerleave', clearCalendarHover);
  }

  render();
  wrap._renderCalendar = render;
  return render;
};

const initFerieSection = (wrap) => {
  const toggle = wrap.querySelector('.js-ferie-toggle');
  const panel = wrap.querySelector('.js-ferie-panel');
  if (!toggle || !panel) return;

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    const isOpen = toggle.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(isOpen));
    panel.hidden = !isOpen;
  });
};

const initFerieTurnusy = (wrap, state, renderCalendar) => {
  const panel = wrap.querySelector('.js-ferie-panel');
  if (!panel || panel.dataset.turnusBound === '1') return;
  panel.dataset.turnusBound = '1';

  FERIE_GROUPS.forEach((group) => {
    const groupWrap = document.createElement('div');
    groupWrap.className = 'offer-search__ferie-turnusy';

    generateFerieTurnusy(group).forEach((slot) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'offer-search__preset offer-search__ferie-turnus js-date-preset';
      button.dataset.start = slot.start;
      button.dataset.end = slot.end;
      button.dataset.tone = slot.tone;
      button.dataset.label = slot.label;
      button.innerHTML = `
        <span class="offer-search__ferie-marker" aria-hidden="true"></span>
        <span class="offer-search__ferie-copy">
          <span class="offer-search__ferie-dates">${i18nOr(getSearchLabels(wrap.closest('.js-offer-search')).turnus_prefix, 'Turnus')}: ${slot.label}</span>
        </span>
      `;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        wrap.querySelectorAll('.js-date-preset').forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');
        state.rangeStart = parseDate(slot.start);
        state.rangeEnd = parseDate(slot.end);
        state.viewMonth = slot.start.slice(0, 7);
        updateDateSummary(wrap, state, { syncRules: false });
        renderCalendar?.();
        refreshSearchRules(wrap.closest('.js-offer-search'), { allowReset: false });
      });
      groupWrap.appendChild(button);
    });

    if (groupWrap.childElementCount) {
      panel.appendChild(groupWrap);
    }
  });
};

const updateDateSummary = (wrap, state, { syncRules = true } = {}) => {
  const summary = wrap.querySelector('.js-date-summary');
  if (!summary) return;
  const root = wrap.closest('.js-offer-search');
  const hotel = root ? isHotelDateSearch(root) : false;
  const isSchool = root?.classList.contains('offer-search--school');
  const phrases = getSearchLabels(root);
  if (!state.rangeStart) {
    summary.textContent = isSchool
      ? i18nOr(phrases.date_summary_school, 'Wybierz termin szkolenia')
      : i18nOr(phrases.date_summary_trip, 'Wybierz termin wyjazdu');
  } else {
    const end = state.rangeEnd || state.rangeStart;
    const startIso = `${state.rangeStart.getFullYear()}-${String(state.rangeStart.getMonth() + 1).padStart(2, '0')}-${String(state.rangeStart.getDate()).padStart(2, '0')}`;
    const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    if (hotel) {
      const termLabel = i18nOr(phrases.date_term, 'Termin:');
      summary.innerHTML = `${termLabel} <b>${formatHotelTermTitle(startIso, endIso, calendarCopy(root).monthsGenitive)}</b>`;
    } else {
      const depart = i18nOr(phrases.date_depart, 'Wyjazd:');
      const back = i18nOr(phrases.date_return, 'Powrót:');
      summary.textContent = `${depart} ${formatDisplayDate(state.rangeStart)} / ${back} ${formatDisplayDate(end)}`;
    }
  }

  if (root) updateFieldDisplay(root, wrap);
  syncDatePresetActive(wrap);
    if (root && syncRules) refreshSearchRules(root, { allowReset: false });
};

const initDateDropdown = (wrap) => {
  const root = wrap.closest('.js-offer-search');
  const isSchool = root?.classList.contains('offer-search--school');
  // Travel + school: no hardcoded turnus — hydrate from URL / data-search-state only (Figma placeholder).
  const currentMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const state = {
    rangeStart: null,
    rangeEnd: null,
    viewMonth: isSchool ? '2026-01' : currentMonthKey(),
    allowSingleDay: false,
  };

  wrap.querySelectorAll('.js-date-preset').forEach((preset) => preset.classList.remove('is-active'));

  const renderCalendar = initCalendar(wrap, state);
  // Do not refreshSearchRules yet — empty selection would become the baseline and
  // URL hydration would look like a destination change (I3) and wipe the date.
  updateDateSummary(wrap, state, { syncRules: false });
  if (isSchool) {
    initFerieSection(wrap);
    initFerieTurnusy(wrap, state, renderCalendar);
  }

  wrap.querySelectorAll('.js-date-preset').forEach((preset) => {
    preset.addEventListener('click', (event) => {
      event.stopPropagation();
      wrap.querySelectorAll('.js-date-preset').forEach((item) => item.classList.remove('is-active'));
      preset.classList.add('is-active');
      state.rangeStart = parseDate(preset.dataset.start);
      state.rangeEnd = parseDate(preset.dataset.end);
      state.viewMonth = preset.dataset.start.slice(0, 7);
      updateDateSummary(wrap, state, { syncRules: false });
      renderCalendar?.();
      refreshSearchRules(root, { allowReset: false });
    });
  });

  wrap._dateState = state;
};

const getDateLabel = (wrap) => {
  const state = wrap._dateState;
  const root = wrap.closest('.js-offer-search');
  if (!state?.rangeStart) {
    return i18nOr(getSearchLabels(root).date_placeholder, '— wybierz termin —');
  }
  const end = state.rangeEnd || state.rangeStart;
  if (root && isHotelDateSearch(root)) {
    const startIso = `${state.rangeStart.getFullYear()}-${String(state.rangeStart.getMonth() + 1).padStart(2, '0')}-${String(state.rangeStart.getDate()).padStart(2, '0')}`;
    const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    return formatHotelTermTitle(startIso, endIso, calendarCopy(root).monthsGenitive);
  }
  return formatRangeLabel(state.rangeStart, end);
};

const AGE_CHEVRON = '<svg class="offer-search__age-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

let childAgeFieldSeq = 0;

const formatChildAgeLabel = (n, labels = {}) => {
  if (n === 1) return i18nOr(labels.age_year, '1 rok');
  if (n >= 2 && n <= 4) return `${n} ${i18nOr(labels.age_years_few, 'lata')}`;
  return `${n} ${i18nOr(labels.age_years_many, 'lat')}`;
};

const clampChildAge = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_CHILD_AGE;
  return Math.min(MAX_CHILD_AGE, Math.max(MIN_CHILD_AGE, Math.round(n)));
};

const getSavedChildAges = (wrap) => (
  [...(wrap.querySelectorAll('.js-search-child-age') || [])].map((el) => clampChildAge(el.value))
);

const closeAgeSelect = (select) => {
  select.classList.remove('is-open');
  const trigger = select.querySelector('.js-search-age-trigger');
  const list = select.querySelector('.js-search-age-list');
  trigger?.setAttribute('aria-expanded', 'false');
  if (list) list.hidden = true;
};

const closeAllAgeSelects = (wrap) => {
  wrap.querySelectorAll('.js-search-age-select.is-open').forEach(closeAgeSelect);
};

const setAgeSelectValue = (select, age) => {
  const next = clampChildAge(age);
  const input = select.querySelector('.js-search-child-age');
  const valueEl = select.querySelector('.js-search-age-value');
  if (input) input.value = String(next);
  if (valueEl) valueEl.textContent = formatChildAgeLabel(next);
  select.dataset.value = String(next);
  select.querySelectorAll('.js-search-age-option').forEach((option) => {
    const selected = Number(option.dataset.value) === next;
    option.classList.toggle('is-selected', selected);
    option.setAttribute('aria-selected', String(selected));
  });
};

const bindAgeSelect = (select) => {
  const trigger = select.querySelector('.js-search-age-trigger');
  const list = select.querySelector('.js-search-age-list');
  if (!trigger || !list) return;

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const willOpen = !select.classList.contains('is-open');
    closeAllAgeSelects(select.closest('[data-dropdown="guests"]') || select.parentElement);
    if (!willOpen) return;
    select.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    list.hidden = false;
    list.querySelector('.js-search-age-option.is-selected')?.focus();
  });

  list.querySelectorAll('.js-search-age-option').forEach((option) => {
    option.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setAgeSelectValue(select, option.dataset.value);
      closeAgeSelect(select);
    });
  });
};

const renderChildAgeFields = (wrap, count, savedValues = []) => {
  const container = wrap.querySelector('.js-child-ages') || wrap.querySelector('.js-child-dobs');
  if (!container) return;

  if (!count) {
    container.innerHTML = '';
    container.hidden = true;
    return;
  }

  container.hidden = false;
  container.innerHTML = '';

  for (let i = 0; i < count; i += 1) {
    const ageLabels = getSearchLabels(wrap.closest('.js-offer-search'));
    const labelText = count === 1
      ? (ageLabels.child_age || 'Wiek dziecka')
      : (ageLabels.child_age_n || 'Wiek dziecka {n}').replace('{n}', String(i + 1));
    const age = clampChildAge(savedValues[i] ?? DEFAULT_CHILD_AGE);
    const options = [];
    for (let n = MIN_CHILD_AGE; n <= MAX_CHILD_AGE; n += 1) {
      const selected = n === age;
      options.push(
        `<li class="offer-search__age-option js-search-age-option${selected ? ' is-selected' : ''}" role="option" tabindex="-1" data-value="${n}" aria-selected="${selected}">${formatChildAgeLabel(n, ageLabels)}</li>`,
      );
    }

    const fieldId = `search-child-age-${++childAgeFieldSeq}`;
    const field = document.createElement('div');
    field.className = 'offer-search__age';
    field.innerHTML = `
      <span class="offer-search__age-label" id="${fieldId}-label">${labelText}</span>
      <div class="offer-search__age-select js-search-age-select" data-value="${age}">
        <input type="hidden" class="js-search-child-age" value="${age}">
        <button
          type="button"
          class="offer-search__age-trigger js-search-age-trigger"
          aria-expanded="false"
          aria-haspopup="listbox"
          aria-controls="${fieldId}-list"
          aria-labelledby="${fieldId}-label"
        >
          <span class="offer-search__age-value js-search-age-value">${formatChildAgeLabel(age)}</span>
          ${AGE_CHEVRON}
        </button>
        <ul class="offer-search__age-list js-search-age-list" id="${fieldId}-list" role="listbox" aria-labelledby="${fieldId}-label" hidden>
          ${options.join('')}
        </ul>
      </div>
    `;
    container.appendChild(field);
    bindAgeSelect(field.querySelector('.js-search-age-select'));
  }
};

const initGuestsDropdown = (wrap) => {
  const adultsEl = wrap.querySelector('.js-guests-adults');
  const childrenEl = wrap.querySelector('.js-guests-children');
  if (!adultsEl || !childrenEl) return;

  const root = wrap.closest('.js-offer-search');
  const defaultChildren = root?.classList.contains('offer-search--school') ? 1 : 2;
  const state = { adults: 2, children: defaultChildren };
  wrap._guestsState = state;

  const sync = () => {
    const previousAges = getSavedChildAges(wrap);
    const root = wrap.closest('.js-offer-search');

    adultsEl.textContent = String(state.adults);
    adultsEl.dataset.value = String(state.adults);
    childrenEl.textContent = String(state.children);
    childrenEl.dataset.value = String(state.children);
    wrap.querySelector('.js-guests-adults-minus').disabled = state.adults <= 1;
    wrap.querySelector('.js-guests-children-minus').disabled = state.children <= 0;

    renderChildAgeFields(wrap, state.children, previousAges.slice(0, state.children));
    if (root) refreshSearchRules(root, { allowReset: false });
  };

  wrap.querySelector('.js-guests-adults-minus')?.addEventListener('click', () => {
    state.adults = Math.max(1, state.adults - 1);
    sync();
  });
  wrap.querySelector('.js-guests-adults-plus')?.addEventListener('click', () => {
    state.adults += 1;
    sync();
  });
  wrap.querySelector('.js-guests-children-minus')?.addEventListener('click', () => {
    state.children = Math.max(0, state.children - 1);
    sync();
  });
  wrap.querySelector('.js-guests-children-plus')?.addEventListener('click', () => {
    state.children += 1;
    sync();
  });

  wrap.addEventListener('click', (event) => {
    if (!event.target.closest('.js-search-age-select')) {
      closeAllAgeSelects(wrap);
    }
  });

  sync();
};

const getGuestsLabel = (wrap) => {
  const state = wrap._guestsState || { adults: 2, children: 0 };
  const labels = getSearchLabels(wrap.closest('.js-offer-search'));
  const adultsLabel = state.adults === 1
    ? i18nOr(labels.guest_adult_one, '1 dorosły')
    : `${state.adults} ${i18nOr(labels.guest_adult_many, 'dorosłych')}`;
  if (!state.children) return adultsLabel;
  const childrenLabel = state.children === 1
    ? i18nOr(labels.guest_child_one, '1 dziecko')
    : `${state.children} ${i18nOr(labels.guest_child_many, 'dzieci')}`;
  return `${adultsLabel} + ${childrenLabel}`;
};

const applyDropdown = (root, wrap) => {
  const type = wrap.dataset.dropdown;
  const valueEl = root.querySelector(`.js-search-value-${type}`);
  if (!valueEl) return;

  updateFieldDisplay(root, wrap);

  if (type === 'date' || type === 'destination' || type === 'type' || type === 'guests') {
    refreshSearchRules(root, { allowReset: type === 'destination' || type === 'type' });
  }

  closeAllDropdowns(root, { immediate: true });

  // Figma sequence: after destination/type, open the next required field (Typ po Dokąd).
  if (type === 'destination' || type === 'type') {
    const next = getFirstBlockingDropdown(root);
    if (next && next !== wrap) {
      openDropdown(root, next);
    }
  }

  if (type === 'type' && tryNavigateFromSearch(root)) {
    return;
  }

  // Listing live-reload dopiero po Dokąd + realnym Typie (nie „Dowolna oferta”).
  if (root.classList.contains('offer-search--school') || isUnifiedListingContext()) {
    const destinations = getSelectedDestinations(root);
    const types = getSelectedTypes(root);
    if (shouldRequireOfferType(getDestinationMode(destinations), types)) {
      return;
    }
    root.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
  }
};

const initDropdowns = (root) => {
  if (root.dataset.dropdownsBound === '1') return;
  root.dataset.dropdownsBound = '1';

  root.querySelectorAll('.js-search-dropdown').forEach((wrap) => {
    const type = wrap.dataset.dropdown;
    if (type === 'destination') initDestinationDropdown(wrap);
    if (type === 'type') initTypeDropdown(wrap);
    if (type === 'date') initDateDropdown(wrap);
    if (type === 'guests') initGuestsDropdown(wrap);

    const trigger = wrap.querySelector('.offer-search__field');
    const panel = wrap.querySelector('.offer-search__popover');

    wrap.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;

    wrap.querySelector('.js-search-apply')?.addEventListener('click', (event) => {
      event.stopPropagation();
      applyDropdown(root, wrap);
    });

    trigger?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!wrap.classList.contains('is-disabled')) return;
      event.preventDefault();
      showDisabledFieldHint(root, wrap);
    });
  });

  const fields = root.querySelector('.offer-search__fields');

  fields?.addEventListener('mousedown', (event) => {
    if (event.button !== 0) return;

    if (isEventInsidePopover(event)) {
      return;
    }

    const field = resolveFieldFromEvent(root, event);
    if (!field) return;

    const wrap = field.closest('.js-search-dropdown');
    if (!wrap) return;

    event.preventDefault();
    event.stopPropagation();
    toggleDropdown(root, wrap);
  }, true);

  document.addEventListener('click', (event) => {
    if (!isEventInsideRoot(root, event)) {
      closeAllDropdowns(root);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const openAge = root.querySelector('.js-search-age-select.is-open');
      if (openAge) {
        event.preventDefault();
        closeAgeSelect(openAge);
        return;
      }
      hideFieldHint();
      closeAllDropdowns(root);
    }
  });
};

const updateHomeOffersIndicator = () => {
  const container = document.querySelector('.js-offers-tabs');
  if (!container) return;

  const indicator = container.querySelector('.home-offers__tab-indicator');
  const active = container.querySelector('.home-offers__tab.is-active');
  if (!indicator || !active) return;

  indicator.style.width = `${active.offsetWidth}px`;
  indicator.style.left = `${active.offsetLeft}px`;
};

const syncSeasonUI = (season) => {
  document.querySelectorAll('.offer-search__season-btn').forEach((btn) => {
    const active = btn.dataset.season === season;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-selected', String(active));
  });

  document.querySelectorAll('.home-offers__tab').forEach((tab) => {
    const active = tab.dataset.tab === season;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', String(active));
  });

  updateHomeOffersIndicator();
};

const initSeasonTabs = () => {
  if (document.body.dataset.seasonTabsBound === '1') return;
  document.body.dataset.seasonTabsBound = '1';

  document.addEventListener('click', (event) => {
    const seasonBtn = event.target.closest('.offer-search__season-btn');
    if (seasonBtn?.dataset.season) {
      syncSeasonUI(seasonBtn.dataset.season);
      document.querySelectorAll('.js-offer-search').forEach((root) => {
        const dateWrap = root.querySelector('[data-dropdown="date"]');
        if (dateWrap) {
          delete dateWrap.dataset.slotsLoaded;
          delete dateWrap.dataset.slotsKey;
          if (dateWrap.classList.contains('is-open')) {
            loadTurnusSlots(root);
          }
        }
      });
      if (isUnifiedListingContext()) {
        document.dispatchEvent(new CustomEvent('hski:search-change', { bubbles: true }));
      }
      return;
    }

    const homeTab = event.target.closest('.home-offers__tab');
    if (homeTab?.dataset.tab) {
      syncSeasonUI(homeTab.dataset.tab);
    }
  });

  updateHomeOffersIndicator();
  window.addEventListener('resize', updateHomeOffersIndicator);
};

const getFirstBlockingDropdown = (root) => {
  const destinationWrap = root.querySelector('[data-dropdown="destination"]');
  const typeWrap = root.querySelector('[data-dropdown="type"]');
  const destinations = getSelectedDestinations(root);
  const mode = getDestinationMode(destinations);
  const types = getSelectedTypes(root);
  const decision = resolveFormAction(destinations, types, getSearchUrls(root));

  if (!destinations.length || mode === 'invalid') {
    return destinationWrap;
  }

  // Spec ofertaTypowWymaga: PL i zagranica wymagają realnego typu (nie „Dowolna oferta”).
  if (shouldRequireOfferType(mode, types)) {
    return typeWrap || destinationWrap;
  }

  if (!decision.valid) {
    return typeWrap || destinationWrap;
  }

  return null;
};

const initSearchFormSubmit = (root) => {
  const form = root.querySelector('.js-offer-search-form');
  if (!form || form.dataset.submitBound === '1') return;
  form.dataset.submitBound = '1';

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const blockingDropdown = getFirstBlockingDropdown(root);
    if (blockingDropdown) {
      closeAllDropdowns(root, { immediate: true });
      openDropdown(root, blockingDropdown);
      const destinations = getSelectedDestinations(root);
      const types = getSelectedTypes(root);
      if (
        blockingDropdown.dataset.dropdown === 'type'
        && shouldRequireOfferType(getDestinationMode(destinations), types)
      ) {
        const trigger = blockingDropdown.querySelector('.offer-search__field');
        showFieldHint(trigger, getSearchLabels(root).hint_need_type_results);
      }
      return;
    }

    const params = collectFromSearchRoot(form);
    const decision = resolveFormAction(
      params.destinations,
      params.types,
      getSearchUrls(root),
    );

    if (!decision.valid || !decision.action) {
      closeAllDropdowns(root, { immediate: true });
      openDropdown(root, root.querySelector('[data-dropdown="destination"]'));
      return;
    }

    const wireParams = {
      ...params,
      intent: decision.intent,
      types: decision.types,
      packageOnly: decision.flags.packageOnly,
      trainingOnly: decision.flags.trainingOnly,
    };

    if (root.classList.contains('offer-search--school') || isUnifiedListingContext()) {
      if (tryNavigateFromSearch(root, { decision, params: wireParams })) {
        return;
      }
      root.dispatchEvent(new CustomEvent('hski:search-change', {
        bubbles: true,
        detail: { scroll: isUnifiedListingContext() },
      }));
      if (isUnifiedListingContext()) {
        return;
      }
    }

    const submitBtn = form.querySelector('[type="submit"], .js-offer-search-submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');
    }

    navigateWithSearchParams(decision.action, wireParams);
  });
};

const initQuickFilters = () => {
  // Handled by bindEvents (delegated change on root).
};

const initSearchFromUrl = (root) => {
  // Prefer data-search-state (SearchContract via Presenter); URL is fallback.
  const hasServerState = Boolean(root.getAttribute('data-search-state'));
  if (!window.location.search && !hasServerState) return;

  const params = resolveInitialSearchParams(root);
  applySearchParamsToRoot(root, params);

  // Seed selection snapshot from hydrated UI so the first refreshSearchRules
  // does not treat dest/type restore as an I3 upstream change (clears date).
  root._searchSelection = captureSelection(
    getSelectedDestinations(root),
    getSelectedTypes(root),
  );

  root.querySelectorAll('.js-search-dropdown').forEach((wrap) => {
    updateFieldDisplay(root, wrap);
    if (wrap.dataset.dropdown === 'date') {
      const state = wrap._dateState;
      if (state) {
        syncDatePresetActive(wrap);
        updateDateSummary(wrap, state, { syncRules: false });
        focusCalendarOnRelevantTerm(wrap);
      }
    }
    if (wrap.dataset.dropdown === 'guests' && wrap._guestsState) {
      const count = wrap._guestsState.children;
      renderChildAgeFields(wrap, count, wrap._pendingChildAges || []);
    }
  });

  refreshSearchRules(root);
};

const initOfferSearch = () => {
  initSeasonTabs();

  document.querySelectorAll('.js-offer-search').forEach((root) => {
    const ui = parseSearchUi(root);
    if (ui) hydrateSearchCatalog(ui);

    initDropdowns(root);
    bindEvents(root);
    initSearchFormSubmit(root);
    initSearchFromUrl(root);
    // Presets load lazily when user opens „Kiedy?” — do not block first paint / Szukaj.

    root.querySelectorAll('.js-search-dropdown').forEach((wrap) => {
      updateFieldDisplay(root, wrap);
    });

    refreshSearchRules(root);
    root.dataset.searchReady = '1';
  });
};

export { refreshSearchRules };
export default initOfferSearch;
